import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import ForgotPassword from "@/components/ForgotPassword";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome, ${u.name}`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-[#060B14] noise-overlay">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden p-12 border-r border-[#1E293B]">
        <div
          className="absolute inset-0 opacity-30"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?w=1600&q=80)", backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#060B14] via-[#060B14]/70 to-transparent" />

        <div className="relative z-10 flex items-center gap-3">
          <img src="/logo.png" alt="Nawab Properties" data-testid="brand-logo" className="w-12 h-12 rounded-lg bg-white object-contain gold-glow" />
          <div>
            <div className="font-serif text-2xl text-white">Nawab Properties</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mt-1">Luxury Real Estate • Mohali</div>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="font-serif text-4xl xl:text-5xl text-white leading-tight">
            A CRM built for <span className="gradient-gold-text">luxury</span> real estate.
          </h1>
          <p className="mt-4 text-slate-300 leading-relaxed">
            Manage leads, schedule site visits, and close deals faster with AI-powered scoring, smart follow-ups, and instant WhatsApp workflows.
          </p>
          <div className="mt-8 flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.25em]">
            <Sparkle weight="duotone" /> Powered by Claude Sonnet 4.5
          </div>
        </div>

        <div className="relative z-10 text-xs text-slate-500 uppercase tracking-widest">
          © 2026 Nawab Properties Pvt. Ltd.
        </div>
      </div>

      {/* Right auth form */}
      <div className="flex-1 flex items-center justify-center p-6">
        {forgot ? <ForgotPassword onBack={() => setForgot(false)} /> : (
        <form onSubmit={submit} className="w-full max-w-md space-y-6 animate-fade-up" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Welcome back</div>
            <h2 className="font-serif text-3xl text-white mt-2">Sign in to your dashboard</h2>
            <p className="text-slate-400 text-sm mt-2">Enter your credentials to continue.</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">Username</label>
              <input
                type="text"
                autoComplete="username"
                placeholder="Username (or email for accounts without one)"
                data-testid="login-email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="mt-2 w-full bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-md px-4 py-3 text-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-slate-400">Password</label>
              <input
                type="password"
                data-testid="login-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="mt-2 w-full bg-[#0A1120] border border-[#1E293B] focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] outline-none rounded-md px-4 py-3 text-white"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full py-3 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold hover:bg-[#F2CE6E] transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-right">
            <button type="button" onClick={() => setForgot(true)} data-testid="forgot-password-link" className="text-xs text-slate-400 hover:text-[#D4AF37]">Forgot password?</button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
